import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import PanelEmptyState from "./PanelEmptyState";
import "./PanelBase.css";
import "./OrdersPanel.css";
import { useToast } from "../ToastProvider";
import { useRouterStatus } from "../../hooks/useRouterStatus.js";
import { useOpenOrdersData } from "./hooks/useOpenOrdersData.js";
import { useOrderStore } from "../../store/orderStore.jsx";
import { useThemeStore } from "../../store/themeStore.jsx";
import { isDarkTheme } from "../../config/themes";
import { shallow } from "zustand/shallow";
import { router } from "../../data/Services/ordersRouter.jsx";
import { getOrderSideColor } from "../../utils/orderStyles.js";
import {
  getOrderStatusDisplay,
  getOtoLegKind,
  getOtoTypeLabel,
} from "../../utils/orderConversions.js";
import { submitOrderToRouter } from "../../utils/orderSubmission.js";
import { QuantityEditModal } from "../Modals";
import {
  cancelAllLocalOrders,
  cancelAllRouterOrders,
} from "../../utils/orderCancellation.js";
import {
  confirmCancelOrder,
  confirmCancelAllOrders,
} from "../../utils/confirmationUtils.js";
import { formatPrice } from "../../utils/priceUtils.js";
import { formatNumber } from "../../utils/numberFormatting.js";
import marketdata from "../../data/Services/marketdata.jsx";
import { buildReplaceOrder } from "../../utils/buildReplaceOrder.js";
import { getNumericFontFamily } from "../../utils/fontUtils.js";
import { PanelToolbar, PanelToolbarHost } from "./PanelToolbar";
import useHostDocument from "~/hooks/useHostDocument";

// Panel that manages and displays open orders with cancel all feature
const OpenOrdersPanel = ({
  initialToolbarPinned = true,
  onToolbarPinnedChange,
  claimFocus,
}) => {
  const { openOrders: routerOrders } = useOpenOrdersData();
  const { showError } = useToast();
  const theme = useThemeStore((state) => state.theme);
  const { isConnected: isRouterConnected } = useRouterStatus();

  // PoC #popout: document the panel currently lives in (menu portal target)
  const [hostDoc, hostProbe] = useHostDocument();
  // Menu state for actions dropdown (same pattern as WorkspaceMenus)
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const [menuVisible, setMenuVisible] = React.useState(false);
  const [menuOrder, setMenuOrder] = React.useState(null);
  const [menuPosition, setMenuPosition] = React.useState({ top: 0, left: 0 });
  const [toolbarPinned, setToolbarPinned] =
    React.useState(initialToolbarPinned);

  // Handle clicks outside menu to close it (always active)
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMenuVisible(false);
        setMenuOrder(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Modal state for order editing
  const [editModalState, setEditModalState] = React.useState({
    visible: false,
    order: null,
  });

  // Get chart orders from all symbols - use shallow comparison to avoid infinite loops
  const ordersBySymbol = useOrderStore(
    (state) => state.ordersBySymbol,
    shallow,
  );
  const pendingModifications = useOrderStore(
    (state) => state.pendingModifications,
  );

  // Use useMemo to create stable array reference with grouped OTO orders
  const chartOrders = useMemo(() => {
    const orders = [];
    Object.entries(ordersBySymbol).forEach(([symbol, symbolOrders]) => {
      symbolOrders.forEach((order) => {
        // Add main order
        orders.push({ ...order, symbol });

        // Add OTO orders immediately after main order (they'll be styled as sub-rows)
        if (order.oto && order.oto.length > 0) {
          const enriched = order.oto.map((leg) => ({
            ...leg,
            // derive kind on demand (stop | profit)
            type: getOtoLegKind(leg),
          }));
          // Sort: profit first then stop (unchanged ordering intent)
          const sortedOTOs = enriched.sort((a, b) => {
            if (a.type === "profit" && b.type === "stop") return -1;
            if (a.type === "stop" && b.type === "profit") return 1;
            return 0;
          });
          sortedOTOs.forEach((otoOrder) => {
            orders.push({
              ...otoOrder,
              symbol,
              mainOrderId: order.id,
              isOTO: true,
              parentOrder: order,
            });
          });
        }
      });
    });
    return orders;
  }, [ordersBySymbol]);

  // Get order store actions for order submission
  const removeOrderFromStore = useOrderStore((state) => state.removeOrder);
  const removeOTOFromOrder = useOrderStore((state) => state.removeOTOFromOrder);

  // Helper function to remove all OTO orders using the same logic as chart interactive
  const removeAllOTOsForOrder = (symbol, mainOrderId) => {
    // Get all OTO orders for this main order
    const allOrders = Object.values(ordersBySymbol).flat();
    const mainOrder = allOrders.find((order) => order.id === mainOrderId);

    if (mainOrder && mainOrder.oto && mainOrder.oto.length > 0) {
      // Remove each OTO order individually to match chart behavior
      mainOrder.oto.forEach((otoOrder) => {
        removeOTOFromOrder(symbol, mainOrderId, otoOrder.id);
      });
    }
  };

  // Merge router orders and chart orders, avoiding duplicates
  const allOrders = useMemo(() => {
    const routerOrderIds = new Set(routerOrders.map((order) => order.id));

    // First, identify OCO groups from router orders
    const ocoGroups = new Map(); // Map<ocoId, [order1, order2]>
    const processedOcoOrders = new Set(); // Track which orders are already processed as OCO

    routerOrders.forEach((routerOrder) => {
      if (routerOrder.oco) {
        // Use a consistent group key for both orders in the OCO pair
        // We'll use the lexicographically smaller ID to ensure both orders use the same group key
        const ocoId = [routerOrder.id, routerOrder.oco].sort()[0];

        if (!ocoGroups.has(ocoId)) {
          ocoGroups.set(ocoId, []);
        }
        ocoGroups.get(ocoId).push(routerOrder);
      }
    });

    // Process router orders to maintain grouping structure (same as chart orders)
    const processedRouterOrders = [];

    // First, process all OCO groups
    ocoGroups.forEach((ocoOrders, ocoId) => {
      if (ocoOrders.length > 1) {
        // Only create groups for actual OCO pairs
        // Sort OCO orders by price (limit orders first, then stop orders)
        const sortedOcoOrders = ocoOrders.sort((a, b) => {
          // Put limit orders (take profit) before stop orders
          if (a.limit && !b.limit) return -1;
          if (!a.limit && b.limit) return 1;
          if (a.limit && b.limit) return b.limit - a.limit; // Higher limit first
          if (a.stop && b.stop) return a.stop - b.stop; // Lower stop first
          return 0;
        });

        // Add OCO group indicator first
        processedRouterOrders.push({
          id: `oco-group-${ocoId}`,
          symbol: sortedOcoOrders[0].symbol,
          tipo: "",
          qtd: "",
          price: "",
          status: `Stop Simultâneo (${sortedOcoOrders.length} ordens)`,
          orderType: "OCO_GROUP",
          isChartOrder: false,
          isOCOGroup: true,
          ocoId: ocoId,
          ocoOrders: sortedOcoOrders,
        });

        // Add each OCO order as sub-rows
        sortedOcoOrders.forEach((ocoOrder, index) => {
          // Check if store has a more recent version (for optimistic updates)
          const storeOrder = chartOrders.find(
            (o) => o.id === ocoOrder.id && !o.isOTO,
          );
          const storeOverlay = {};
          if (storeOrder) {
            storeOverlay.price = storeOrder.price;
            storeOverlay.qtd = storeOrder.quantity;
            // OCO render uses limit || stop for display — update the relevant field
            if (storeOrder.orderType === "LIMIT") {
              storeOverlay.limit = storeOrder.price;
            } else if (storeOrder.orderType === "STOPLIMIT") {
              storeOverlay.stop = storeOrder.price;
            }
          }
          processedRouterOrders.push({
            ...ocoOrder,
            ...storeOverlay,
            isChartOrder: false,
            isOCO: true,
            ocoId: ocoId,
            ocoIndex: index + 1,
            ocoTotal: sortedOcoOrders.length,
          });
          processedOcoOrders.add(ocoOrder.id);
        });
      }
    });

    // Then, process remaining non-OCO orders
    routerOrders.forEach((routerOrder) => {
      // Skip if already processed as part of OCO group
      if (processedOcoOrders.has(routerOrder.id)) {
        return;
      }

      // Check if store has a more recent version of this order (for optimistic updates)
      const storeOrder = chartOrders.find(
        (o) => o.id === routerOrder.id && !o.isOTO,
      );

      // Add regular main order (non-OCO)
      // Prefer store's price/quantity when available (reflects optimistic chart updates)
      processedRouterOrders.push({
        ...routerOrder,
        ...(storeOrder && {
          price: storeOrder.price,
          qtd: storeOrder.quantity,
        }),
        isChartOrder: false,
      });

      // Use store's OTO if available (has optimistic updates), otherwise use router's OTO
      const otoSource = storeOrder?.oto || routerOrder.originalOrder?.oto;

      // Add OTO orders immediately after main order (using oto array like chart orders)
      if (otoSource && otoSource.length > 0) {
        const enriched = otoSource.map((leg) => ({
          ...leg,
          // derive kind on demand (stop | profit)
          type: getOtoLegKind(leg),
        }));

        // Sort: profit first then stop (same as chart orders)
        const sortedOTOs = enriched.sort((a, b) => {
          if (a.type === "profit" && b.type === "stop") return -1;
          if (a.type === "stop" && b.type === "profit") return 1;
          return 0;
        });

        sortedOTOs.forEach((otoOrder) => {
          processedRouterOrders.push({
            id: otoOrder.id,
            symbol: routerOrder.symbol,
            tipo: `${getOtoTypeLabel(otoOrder.type)} (${otoOrder.side === "buy" ? "COMPRA" : "VENDA"})`,
            qtd: otoOrder.quantity,
            price: otoOrder.price,
            status: getOrderStatusDisplay({ state: otoOrder.state || "open" }),
            orderType:
              otoOrder.orderType || (otoOrder.stop ? "STOPLIMIT" : "LIMIT"),
            isChartOrder: false,
            isOTO: true,
            mainOrderId: routerOrder.id,
            parentOrder: storeOrder || routerOrder.originalOrder,
            originalOrder: otoOrder,
          });
        });
      }
    });

    // Convert chart orders to display format
    const chartOrdersForDisplay = chartOrders
      .filter((order) => !routerOrderIds.has(order.id)) // Avoid duplicates
      .map((order) => {
        // Handle OTO orders differently
        if (order.isOTO) {
          const otoTypeLabel = getOtoTypeLabel(order.type);
          return {
            id: order.id,
            symbol: order.symbol,
            tipo: `${otoTypeLabel} (${order.side === "buy" ? "COMPRA" : "VENDA"})`,
            qtd: order.quantity,
            price: order.price,
            // Store a neutral base; final label derived in render via parentState
            status: "LOCAL",
            orderType: order.type === "stop" ? "STOPLIMIT" : "LIMIT",
            isChartOrder: true,
            isOTO: true,
            mainOrderId: order.mainOrderId,
            parentOrder: order.parentOrder, // Keep parent order reference
            originalOrder: order,
          };
        }

        // Handle main orders
        return {
          id: order.id,
          symbol: order.symbol,
          tipo: order.side === "buy" ? "COMPRA" : "VENDA",
          qtd: order.quantity,
          price: order.price,
          status: getOrderStatusDisplay(order),
          orderType: order.orderType || "MARKET",
          isChartOrder: true,
          originalOrder: order,
        };
      });

    // Combine all orders and sort by main order price (descending)
    const allOrdersList = [...processedRouterOrders, ...chartOrdersForDisplay];

    // Group orders by logical units and sort main orders by price (descending)
    const sortedOrders = [];
    const processedOrderIds = new Set();

    // Separate orders into logical groups: main orders, OCO groups, and standalone orders
    const mainOrders = allOrdersList.filter(
      (order) => !order.isOTO && !order.isOCO && !order.isOCOGroup,
    );
    const ocoGroupOrders = allOrdersList.filter((order) => order.isOCOGroup);

    // Sort main orders by price descending
    const sortedMainOrders = mainOrders.sort(
      (a, b) => (b.price || 0) - (a.price || 0),
    );

    // Sort OCO groups by the highest price in the group (for positioning among main orders)
    const sortedOcoGroups = ocoGroupOrders.sort((a, b) => {
      const maxPriceA = Math.max(
        ...a.ocoOrders.map((o) => o.price || o.limit || o.stop || 0),
      );
      const maxPriceB = Math.max(
        ...b.ocoOrders.map((o) => o.price || o.limit || o.stop || 0),
      );
      return maxPriceB - maxPriceA;
    });

    // Combine and sort all groups by their representative price
    const allGroups = [
      ...sortedMainOrders.map((order) => ({
        type: "main",
        order,
        price: order.price || 0,
      })),
      ...sortedOcoGroups.map((group) => ({
        type: "oco",
        order: group,
        price: Math.max(
          ...group.ocoOrders.map((o) => o.price || o.limit || o.stop || 0),
        ),
      })),
    ].sort((a, b) => b.price - a.price);

    // Build final sorted order list maintaining group integrity
    allGroups.forEach((group) => {
      if (group.type === "main") {
        const mainOrder = group.order;
        if (processedOrderIds.has(mainOrder.id)) return;

        // Add main order
        sortedOrders.push(mainOrder);
        processedOrderIds.add(mainOrder.id);

        // Add OTO orders for this main order (preserve their existing order)
        const otoOrders = allOrdersList.filter(
          (order) => order.isOTO && order.mainOrderId === mainOrder.id,
        );
        otoOrders.forEach((otoOrder) => {
          if (!processedOrderIds.has(otoOrder.id)) {
            sortedOrders.push(otoOrder);
            processedOrderIds.add(otoOrder.id);
          }
        });
      } else if (group.type === "oco") {
        const ocoGroup = group.order;
        if (processedOrderIds.has(ocoGroup.id)) return;

        // Add OCO group header
        sortedOrders.push(ocoGroup);
        processedOrderIds.add(ocoGroup.id);

        // Add OCO sub-orders (already sorted within the group)
        const ocoSubOrders = allOrdersList.filter(
          (order) => order.isOCO && order.ocoId === ocoGroup.ocoId,
        );
        ocoSubOrders.forEach((ocoOrder) => {
          if (!processedOrderIds.has(ocoOrder.id)) {
            sortedOrders.push(ocoOrder);
            processedOrderIds.add(ocoOrder.id);
          }
        });
      }
    });

    return sortedOrders;
  }, [routerOrders, chartOrders]);

  const handleCancelOrders = async () => {
    // Use confirmation utility that respects user settings
    confirmCancelAllOrders(async () => {
      try {
        // Cancel all router orders using centralized utility
        await cancelAllRouterOrders();

        // Cancel all local orders using centralized utility
        cancelAllLocalOrders(ordersBySymbol, removeOrderFromStore);

        // Success message will come from server via receiveInfo
      } catch (err) {
        console.error("❌ Erro ao cancelar ordens:", err);
        showError(
          `Erro ao cancelar ordens: ${err.message}`,
          "Erro de Cancelamento",
        );
        if (
          err.message.includes("401") ||
          err.message.includes("Unauthorized") ||
          err.message.includes("login")
        ) {
          // Request footer to open router login modal
          window.dispatchEvent(new CustomEvent("openRouterLogin"));
        }
      }
    });
  };

  // Handle individual order cancellation
  const handleCancelOrder = async (order) => {
    // Handle CHART OTO orders specially - remove ALL OTO orders (same as chart behavior)
    // Only for LOCAL chart orders, not router orders
    if (order.isOTO && order.mainOrderId && order.isChartOrder) {
      // For chart OTO orders, remove ALL OTO orders from the main order (like chart interactive)
      removeAllOTOsForOrder(order.symbol, order.mainOrderId);
      return;
    }

    if (order.isChartOrder) {
      // For chart orders, just remove from local store
      removeOrderFromStore(order.symbol, order.originalOrder.id);
      return;
    }

    // For router orders, cancel via router with confirmation
    confirmCancelOrder(order.id, async () => {
      try {
        // Use originalOrder (contains proper router format) instead of display object
        await router.cancelOrder(order.originalOrder || order);
      } catch (err) {
        console.error("❌ Erro ao cancelar ordem:", err);
        if (
          err.message.includes("401") ||
          err.message.includes("Unauthorized") ||
          err.message.includes("login")
        ) {
          window.dispatchEvent(new CustomEvent("openRouterLogin"));
        } else if (
          err.message?.includes("não existir") ||
          err.message?.includes("já foi cancelada")
        ) {
          // #354: não remove a linha (o bool-false genérico não prova que sumiu);
          // só resync. Busta o cache de dedup p/ o snapshot não ser engolido (#10).
          console.log(
            "🔄 Order may no longer exist, refreshing account data to sync state",
          );
          if (router && router.getAccountDataAsync) {
            router._lastAccountDataString = null;
            router
              .getAccountDataAsync()
              .catch((e) => console.warn("Failed to refresh account data:", e));
          }
        } else {
          alert(`Erro ao cancelar ordem: ${err.message}`);
        }
      }
    });
  };

  // Handle OCO order cancellation
  const handleCancelOCOOrder = async (order) => {
    // For OCO orders from router, cancel via router with confirmation
    confirmCancelOrder(order.id, async () => {
      try {
        // Use originalOrder (contains proper router format) instead of display object
        await router.cancelOrder(order.originalOrder || order);
      } catch (err) {
        console.error("❌ Erro ao cancelar ordem OCO:", err);
        if (
          err.message.includes("401") ||
          err.message.includes("Unauthorized") ||
          err.message.includes("login")
        ) {
          window.dispatchEvent(new CustomEvent("openRouterLogin"));
        } else if (
          err.message?.includes("não existir") ||
          err.message?.includes("já foi cancelada")
        ) {
          // #354: não remove a linha (o bool-false não prova que sumiu); só resync.
          // Busta o cache de dedup p/ o snapshot não ser engolido (#10) — espelha
          // handleCancelOrder.
          console.log(
            "🔄 OCO order may no longer exist, refreshing account data to sync state",
          );
          if (router && router.getAccountDataAsync) {
            router._lastAccountDataString = null;
            router
              .getAccountDataAsync()
              .catch((e) => console.warn("Failed to refresh account data:", e));
          }
        } else {
          alert(`Erro ao cancelar ordem OCO: ${err.message}`);
        }
      }
    });
  };

  // Handle chart order actions
  const handleSubmitLocalOrder = async (order) => {
    if (order.isChartOrder && order.originalOrder.state === "local") {
      // Submit to router using shared utility
      const submitted = await submitOrderToRouter(
        order.originalOrder,
        () => window.dispatchEvent(new CustomEvent("openRouterLogin")), // Callback to open login modal in Footer
        () => {
          // Success callback - remove order from chart orders since it's now handled by router
          removeOrderFromStore(
            order.originalOrder.symbol,
            order.originalOrder.id,
          );
        },
        (error) => {
          // Error callback
          console.error("❌ Failed to submit order:", error);
          alert(`Erro ao enviar ordem: ${error.message}`);
        },
      );

      return submitted;
    }
  };

  // Handle edit button click to edit order
  const handleEditOrder = (order) => {
    // Only allow editing for orders that can be modified
    if (order.isChartOrder && order.originalOrder.state === "local") {
      // For local chart orders, open the edit modal
      setEditModalState({
        visible: true,
        order: order.originalOrder,
      });
    } else if (!order.isChartOrder && order.originalOrder) {
      // For router orders, prefer the store order (single source of truth).
      // The store order has up-to-date price, quantity, originalRouterOrder (Phase 1),
      // and OTO legs from optimistic updates.
      const storeOrders =
        useOrderStore.getState().ordersBySymbol[order.symbol] || [];
      const storeOrder = storeOrders.find((o) => o.id === order.id);

      let orderForModal;
      if (storeOrder) {
        orderForModal = { ...storeOrder, state: storeOrder.state || "open" };
      } else {
        // Fallback to the panel's converted order (from accountData)
        orderForModal = { ...order.originalOrder, state: "open" };
      }

      setEditModalState({
        visible: true,
        order: orderForModal,
      });
    } else {
      console.error("❌ Order cannot be edited:", order);
    }
  };

  // Handle modal confirmation for order updates
  const handleModalConfirm = async (newQuantity, newPrice, otoData = null) => {
    const order = editModalState.order;

    try {
      if (order.state === "local") {
        // For local orders, update in the store
        const updateOrder = useOrderStore.getState().updateOrder;
        updateOrder(order.symbol, order.id, {
          quantity: newQuantity,
          price: newPrice,
        });

        // Sync OTO leg quantities when main order quantity changes
        if (order.oto && order.oto.length > 0) {
          const updateOTOInOrder = useOrderStore.getState().updateOTOInOrder;

          // Always sync quantity so OTO legs match main order
          order.oto.forEach((leg) => {
            updateOTOInOrder(order.symbol, order.id, leg.id, {
              quantity: newQuantity,
            });
          });

          // Update OTO prices if provided
          if (otoData) {
            if (otoData.stopId && otoData.stopPrice) {
              updateOTOInOrder(order.symbol, order.id, otoData.stopId, {
                price: otoData.stopPrice,
              });
            }
            if (otoData.profitId && otoData.profitPrice) {
              updateOTOInOrder(order.symbol, order.id, otoData.profitId, {
                price: otoData.profitPrice,
              });
            }
          }
        }
      } else if (order.state === "open") {
        // Convert otoData format to otoUpdates array for buildReplaceOrder
        let otoUpdates;
        if (otoData && otoData.profitId && otoData.stopId) {
          otoUpdates = [
            { otoId: otoData.profitId, newPrice: otoData.profitPrice },
            { otoId: otoData.stopId, newPrice: otoData.stopPrice },
          ];
        }

        // Resolve to the concrete contract for the router. Display resolver
        // (non-gated): modifying an existing order must never be blocked by a
        // stale symbol table — mirrors cancel. (#351 review)
        const translatedSymbol = marketdata.resolveSymbol(order.symbol);

        const replaceOrderObject = buildReplaceOrder(
          order,
          {
            price: newPrice,
            quantity: newQuantity,
            otoUpdates,
          },
          translatedSymbol,
        );

        if (import.meta.env.DEV) {
          console.log(
            "🔧 [handleModalConfirm] replace order:",
            JSON.stringify(replaceOrderObject),
          );
        }

        await router.replaceOrder(replaceOrderObject);

        // OCO: backend treats both legs as a single operation —
        // the new quantity applies to the entire OCO pair.
        // Optimistically sync the sibling leg's quantity in the store
        // so the panel reflects the change immediately.
        if (order.oco) {
          const updateOrder = useOrderStore.getState().updateOrder;
          updateOrder(order.symbol, order.id, { quantity: newQuantity });
          updateOrder(order.symbol, order.oco, { quantity: newQuantity });
        }
      }

      // Close the modal
      setEditModalState({ visible: false, order: null });
    } catch (error) {
      console.error("❌ Failed to update order via modal:", error);
      showError(`Erro ao modificar ordem: ${error.message || error}`);
    }
  };

  const isDarkMode = isDarkTheme(theme);
  const handleToolbarPinnedChange = React.useCallback(
    (pinned) => {
      setToolbarPinned(pinned);
      onToolbarPinnedChange?.(pinned);
    },
    [onToolbarPinnedChange],
  );

  return (
    <PanelToolbarHost
      pinned={toolbarPinned}
      onMouseDown={claimFocus}
      className="vertical-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        // Token-driven: navy on Extra Dark, slate on lara-dark, none on light.
        backgroundColor: isDarkMode
          ? "var(--tz-surface-deep, #1f2937)"
          : undefined,
      }}
      contentStyle={{
        padding: "8px",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minHeight: 0,
        overflow: "hidden",
      }}
      toolbar={
        <PanelToolbar
          pinned={toolbarPinned}
          onPinnedChange={handleToolbarPinnedChange}
          pinDataEvoId="openorderspanel-btn-toolbar-pin"
          right={
            <Button
              label="Cancelar Ordens"
              className="btn-vertical-panel"
              onClick={handleCancelOrders}
              data-evo-id="openorderspanel-btn-cancel-all"
            />
          }
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <h5>Ordens Abertas</h5>
            {allOrders.length > 0 && (
              <span className="panel-badge">{allOrders.length}</span>
            )}
          </div>
        </PanelToolbar>
      }
    >
      {allOrders.length === 0 ? (
        <PanelEmptyState
          message="Nenhuma ordem"
          isRouterConnected={isRouterConnected}
          dataEvoId="openorderspanel-link-router-login"
        />
      ) : (
        <DataTable
          value={allOrders}
          className="ordens-datatable log-datatable"
          scrollable
          scrollHeight="flex"
          size="small"
          stripedRows
          rowClassName={(rowData) => {
            const classes = [];
            if (rowData.isOCOGroup) classes.push("oco-group-row");
            else if (rowData.isOCO) classes.push("oco-row");
            else if (rowData.isOTO) classes.push("oto-row");
            else classes.push("main-row");
            // Add pending class for orders being modified
            const orderId = rowData.isOTO ? rowData.mainOrderId : rowData.id;
            if (pendingModifications.has(orderId)) {
              classes.push("order-pending");
            }
            return classes.join(" ");
          }}
        >
          <Column
            field="symbol"
            header="Símbolo"
            style={{ width: "18%" }}
            body={(rowData) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: rowData.isOTO
                    ? "20px"
                    : rowData.isOCO
                      ? "15px"
                      : "0px", // Indent OTO and OCO orders
                }}
              >
                {rowData.isOCOGroup ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      {rowData.symbol}
                    </span>
                  </div>
                ) : rowData.isOTO ? (
                  <div
                    style={{
                      display: "inline-block",
                      padding: "3px 8px",
                      backgroundColor:
                        rowData.originalOrder.type === "profit"
                          ? "#059669"
                          : "#dc2626",
                      color: "white",
                      borderRadius: "4px",
                      fontSize: "9px",
                      fontWeight: "700",
                      fontFamily:
                        "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {getOtoTypeLabel(rowData.originalOrder.type)}
                  </div>
                ) : !rowData.isOCO ? (
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                    }}
                  >
                    {rowData.symbol}
                  </span>
                ) : rowData.isOCO ? (
                  <div
                    style={{
                      display: "inline-block",
                      padding: "3px 8px",
                      backgroundColor:
                        rowData.ocoIndex === 1 ? "#059669" : "#dc2626",
                      color: "white",
                      borderRadius: "4px",
                      fontSize: "9px",
                      fontWeight: "700",
                      fontFamily:
                        "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {rowData.ocoIndex === 1 ? "GANHO" : "PERDA"}
                  </div>
                ) : null}
              </div>
            )}
          />
          <Column
            field="tipo"
            header="C/V"
            style={{ width: "7%", textAlign: "center" }}
            headerClassName="header-center"
            body={(rowData) => {
              // Handle OCO group header
              if (rowData.isOCOGroup) {
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "10px",
                      fontWeight: "600",
                      color: "#ff6b35",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>OCO</span>
                  </div>
                );
              }

              // Handle OCO orders
              if (rowData.isOCO) {
                const side = rowData.action;
                const sideColor = getOrderSideColor(side);
                const sideText = side === "buy" ? "C" : "V";

                return (
                  <div
                    style={{
                      display: "inline-block",
                      padding: "2px 6px",
                      backgroundColor: sideColor,
                      color: "white",
                      borderRadius: "3px",
                      fontSize: "11px",
                      fontWeight: "700",
                      fontFamily:
                        "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif",
                    }}
                  >
                    {sideText}
                  </div>
                );
              }

              // Handle OTO orders
              if (rowData.isOTO) {
                const otoSide = rowData.originalOrder.side;
                const otoColor = getOrderSideColor(otoSide);
                const sideText = otoSide === "buy" ? "C" : "V";

                return (
                  <div
                    style={{
                      display: "inline-block",
                      padding: "2px 6px",
                      backgroundColor: otoColor,
                      color: "white",
                      borderRadius: "3px",
                      fontSize: "11px",
                      fontWeight: "700",
                      fontFamily:
                        "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif",
                    }}
                  >
                    {sideText}
                  </div>
                );
              }

              // Handle main orders
              const side = rowData.tipo.includes("COMPRA") ? "buy" : "sell";
              const sideColor = getOrderSideColor(side);
              const sideText = side === "buy" ? "C" : "V";

              return (
                <div
                  style={{
                    display: "inline-block",
                    padding: "2px 6px",
                    backgroundColor: sideColor,
                    color: "white",
                    borderRadius: "3px",
                    fontSize: "11px",
                    fontWeight: "700",
                    fontFamily:
                      "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif",
                  }}
                >
                  {sideText}
                </div>
              );
            }}
          />
          <Column
            field="qtd"
            header="Qtd"
            style={{ width: "12%", textAlign: "right" }}
            headerClassName="header-right"
            body={(rowData) => (
              <span
                style={{
                  fontSize: "11px",
                  fontFamily: getNumericFontFamily(),
                }}
              >
                {rowData.isOCOGroup ? "" : formatNumber(rowData.qtd, 0)}
              </span>
            )}
          />
          <Column
            field="price"
            header="Preço"
            style={{ width: "14%", textAlign: "right" }}
            headerClassName="header-right"
            body={(rowData) => {
              if (rowData.isOCOGroup) return "";

              // Handle OCO orders - show limit or stop price
              if (rowData.isOCO) {
                const price = rowData.limit || rowData.stop;
                return (
                  <span
                    style={{
                      fontSize: "11px",
                      fontFamily: getNumericFontFamily(),
                    }}
                  >
                    {price ? formatPrice(rowData.symbol, price) : "MKT"}
                  </span>
                );
              }

              return (
                <span
                  style={{
                    fontSize: "11px",
                    fontFamily: getNumericFontFamily(),
                  }}
                >
                  {rowData.price > 0
                    ? formatPrice(rowData.symbol, rowData.price)
                    : "MKT"}
                </span>
              );
            }}
          />
          <Column
            field="total"
            header="Total"
            style={{ width: "15%", textAlign: "right" }}
            headerClassName="header-right"
            body={(rowData) => {
              if (
                rowData.isOCOGroup ||
                !rowData.qtd ||
                !rowData.price ||
                rowData.price <= 0
              )
                return "";
              // FOQ do servidor (getFOQ, autoridade única). Indisponível →
              // célula vazia, nunca um total com multiplicador chutado.
              const foq = rowData.symbol
                ? marketdata.getFOQ(rowData.symbol)
                : null;
              if (foq === null || foq === undefined) return "";
              const total = rowData.qtd * rowData.price * foq;
              return (
                <span
                  style={{
                    fontSize: "11px",
                    fontFamily: getNumericFontFamily(),
                  }}
                >
                  {formatNumber(total, 2)}
                </span>
              );
            }}
          />
          <Column
            field="status"
            header="Status"
            style={{ width: "20%" }}
            body={(rowData) => {
              if (rowData.isOCOGroup) {
                return (
                  <span
                    style={{
                      fontSize: "10px",
                      fontWeight: "600",
                      color: "#ff6b35",
                    }}
                  >
                    Stop Sim.
                  </span>
                );
              }

              // Get order type for display
              let displayType = "MKT";
              if (rowData.isOTO || rowData.isOCO) {
                // OTO/OCO legs are always StopLimit at B3 — display as STP
                displayType = "STP";
              } else {
                const orderType = rowData.isChartOrder
                  ? rowData.originalOrder?.orderType || "MARKET"
                  : rowData.orderType || "MARKET";
                if (orderType === "LIMIT") displayType = "LMT";
                else if (orderType === "STOPLIMIT") displayType = "STP";
              }

              // Get status
              let displayStatus = rowData.status;
              if (rowData.isOTO) {
                const parentState = rowData.parentOrder?.state;
                if (parentState === "open" || parentState === "pending") {
                  displayStatus = "PEND.";
                } else if (parentState === "local") {
                  displayStatus = "LOCAL";
                }
              } else if (rowData.isOCO) {
                displayStatus = getOrderStatusDisplay({
                  state: rowData.estado || "open",
                });
              }

              return (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <span
                    style={{
                      color: getStatusColor(displayStatus),
                      fontSize: "10px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                    }}
                  >
                    {displayStatus}
                  </span>
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: "500",
                      color: "#9ca3af",
                      textTransform: "uppercase",
                    }}
                  >
                    {displayType}
                  </span>
                </div>
              );
            }}
          />
          <Column
            header=""
            style={{ width: "8%", textAlign: "center" }}
            headerClassName="header-center"
            body={(rowData) => {
              // Check if this row should show a gear (has any actions)
              const hasActions =
                rowData.isOCOGroup ||
                rowData.isOCO ||
                (rowData.isOTO && rowData.parentOrder?.state !== "open") ||
                !rowData.isOTO;

              if (!hasActions) return null;

              return (
                <Button
                  icon="pi pi-cog"
                  size="small"
                  text
                  rounded
                  className="gear-btn"
                  style={{
                    width: "22px",
                    height: "22px",
                    fontSize: "12px",
                    padding: "0",
                  }}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    menuButtonRef.current = e.currentTarget;
                    setMenuOrder(rowData);
                    setMenuPosition({ top: rect.bottom + 2, left: rect.right });
                    setMenuVisible(true);
                  }}
                  aria-haspopup
                  data-evo-id="openorderspanel-btn-actions"
                />
              );
            }}
          />
        </DataTable>
      )}

      {hostProbe}
      {/* Custom Menu - same pattern as WorkspaceMenus */}
      {menuVisible &&
        menuOrder &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="p-menu p-component p-menu-overlay"
            style={{
              position: "fixed",
              top: menuPosition.top,
              left: menuPosition.left,
              transform: "translateX(-100%)",
              minWidth: 140,
              zIndex: 10000,
              backgroundColor: "var(--surface-overlay)",
              borderRadius: "4px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
              border: "1px solid var(--surface-border)",
            }}
            onMouseLeave={() => {
              setMenuVisible(false);
              setMenuOrder(null);
            }}
          >
            <ul
              className="p-menu-list"
              style={{ margin: 0, padding: "0.25rem 0", listStyle: "none" }}
            >
              {(() => {
                // Build menu items based on order type
                const items = [];

                // Helper to execute action with validation
                const executeAction = (action) => {
                  const order = menuOrder;
                  setMenuVisible(false);
                  setMenuOrder(null);

                  // Validate order still exists
                  if (!order || !order.id) {
                    console.warn("Order no longer exists, skipping action");
                    return;
                  }
                  const stillExists = allOrders.some((o) => o.id === order.id);
                  if (!stillExists) {
                    console.warn(
                      "Order was removed, skipping action:",
                      order.id,
                    );
                    return;
                  }
                  action(order);
                };

                // OCO group - only cancel
                if (menuOrder.isOCOGroup) {
                  items.push({
                    label: "Cancelar OCO",
                    icon: "pi pi-times",
                    action: () => executeAction(handleCancelOCOOrder),
                  });
                }
                // OCO legs - only edit
                else if (menuOrder.isOCO) {
                  items.push({
                    label: "Editar",
                    icon: "pi pi-pencil",
                    action: () => executeAction(handleEditOrder),
                  });
                }
                // OTO orders
                else if (menuOrder.isOTO) {
                  if (menuOrder.parentOrder?.state !== "open") {
                    items.push({
                      label: "Remover",
                      icon: "pi pi-times",
                      action: () => executeAction(handleCancelOrder),
                    });
                  }
                }
                // Main orders
                else {
                  items.push({
                    label: menuOrder.isChartOrder ? "Remover" : "Cancelar",
                    icon: "pi pi-times",
                    action: () => executeAction(handleCancelOrder),
                  });
                  items.push({
                    label: "Editar",
                    icon: "pi pi-pencil",
                    action: () => executeAction(handleEditOrder),
                  });
                  if (menuOrder.isChartOrder && menuOrder.status === "LOCAL") {
                    items.push({
                      label: "Enviar",
                      icon: "pi pi-arrow-right",
                      action: () => executeAction(handleSubmitLocalOrder),
                    });
                  }
                }

                return items.map((item, index) => (
                  <li key={index} className="p-menuitem">
                    <a
                      className="p-menuitem-link"
                      onClick={(e) => {
                        e.preventDefault();
                        item.action();
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.5rem 1rem",
                        cursor: "pointer",
                      }}
                      data-evo-id="openorderspanel-link-context-menu-item"
                    >
                      {item.icon && (
                        <span className={`p-menuitem-icon ${item.icon}`} />
                      )}
                      <span className="p-menuitem-text">{item.label}</span>
                    </a>
                  </li>
                ));
              })()}
            </ul>
          </div>,
          (hostDoc ?? document).body,
        )}
      <QuantityEditModal
        visible={editModalState.visible}
        onHide={() => setEditModalState({ visible: false, order: null })}
        onConfirm={handleModalConfirm}
        initialQuantity={editModalState.order?.quantity || 0}
        initialPrice={editModalState.order?.price || 0}
        orderType={editModalState.order?.orderType || "MARKET"}
        symbol={editModalState.order?.symbol || ""}
        initialOrder={editModalState.order} // Pass complete order object for OTO detection
        title="Editar Ordem"
        confirmLabel="Atualizar"
        cancelLabel="Cancelar"
      />

      {/* RouterLoginModal is owned by Footer; OpenOrdersPanel requests it via CustomEvent 'openRouterLogin' */}
    </PanelToolbarHost>
  );

  // Helper function for status colors
  function getStatusColor(status) {
    switch (status) {
      case "LOCAL":
        return "#ffa500"; // Orange for local orders
      case "PEND.":
        return "#ffa500"; // Same color as local (scheduled stop)
      case "PROC.":
        return "#ea580c";
      case "ATIVA":
        return "#16a34a";
      case "PARCIAL":
        return "#6366f1";
      default:
        return "#6b7280";
    }
  }
};

OpenOrdersPanel.propTypes = {
  initialToolbarPinned: PropTypes.bool,
  onToolbarPinnedChange: PropTypes.func,
  claimFocus: PropTypes.func,
};

export default React.memo(OpenOrdersPanel);
